import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, AlertTriangle, XCircle, Mail } from 'lucide-react';
import { api } from '../../lib/api';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';
  const [resendSuccess, setResendSuccess] = useState(false);

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/verify-email', { token, email });
      return response.data;
    },
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/resend-verification', { email });
      return response.data;
    },
    onSuccess: () => setResendSuccess(true),
  });

  useEffect(() => {
    if (token && email) {
      verifyMutation.mutate();
    }
  }, []);

  // Missing params
  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-950 px-4">
        <div className="w-full max-w-md glass rounded-2xl p-8 border border-surface-700 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-surface-100 mb-2">Link Inválido</h2>
          <p className="text-surface-400 mb-6">
            O link de verificação é inválido ou está incompleto.
          </p>
          <Link to="/login" className="text-primary-400 hover:text-primary-300">
            Ir para Login
          </Link>
        </div>
      </div>
    );
  }

  // Loading
  if (verifyMutation.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-950 px-4">
        <div className="w-full max-w-md glass rounded-2xl p-8 border border-surface-700 text-center">
          <Loader2 className="w-12 h-12 text-primary-400 animate-spin mx-auto mb-4" />
          <p className="text-surface-400">Verificando seu email...</p>
        </div>
      </div>
    );
  }

  // Success
  if (verifyMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-950 px-4">
        <div className="w-full max-w-md glass rounded-2xl p-8 border border-surface-700 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-surface-100 mb-2">Email Verificado!</h2>
          <p className="text-surface-400 mb-6">
            Seu email foi confirmado com sucesso. Você já pode fazer login.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Ir para Login
          </Link>
        </div>
      </div>
    );
  }

  // Error
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-950 px-4">
      <div className="w-full max-w-md glass rounded-2xl p-8 border border-surface-700 text-center">
        <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-surface-100 mb-2">Verificação Falhou</h2>
        <p className="text-surface-400 mb-6">
          O link de verificação é inválido ou expirou.
        </p>

        {resendSuccess ? (
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg mb-4">
            <div className="flex items-center justify-center gap-2 text-green-400">
              <Mail className="w-4 h-4" />
              <span className="text-sm">Novo email de verificação enviado!</span>
            </div>
          </div>
        ) : (
          <button
            onClick={() => resendMutation.mutate()}
            disabled={resendMutation.isPending}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 mb-4"
          >
            {resendMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Reenviando...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Reenviar Email de Verificação
              </>
            )}
          </button>
        )}

        {resendMutation.isError && (
          <p className="text-red-400 text-sm mb-4">
            Erro ao reenviar. Faça login e tente novamente.
          </p>
        )}

        <div className="mt-2">
          <Link to="/login" className="text-surface-400 hover:text-surface-300 text-sm">
            Voltar ao Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
